"use client";
import { Input } from "@/components/ui/input";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { formatInr } from "@/utils/basicFunctions.util";
import { useId } from "react";
import { useSavingsScheme } from "./hooks";

export interface SavingsSchemeData {
  ariaLabel: string;
  amountLabel: string;
  initialAmount: number;
  amountStep: number;
  monthsLabel: string;
  monthsAriaLabel: string;
  initialMonths: number;
  timelineLabel: string;
  customerLabel: string;
  jewellerLabel: string;
  pays: { label: string; note: string };
  gets: { label: string; note: string };
  // `note` uses {pct}.
  adds: { label: string; note: string };
  states: {
    notStarted: string;
    // Uses {paid} and {total}.
    paying: string;
    allPaid: string;
    // Uses {total}.
    matured: string;
  };
}

const MONTHS = 12;
const cardClass = "grid content-start gap-1 rounded-[22px] p-[18px]";
const cardKeyClass = "text-[14px] font-bold text-ink-muted";
const cardValueClass = "text-[2rem] leading-tight font-bold tracking-[-0.02em]";

// "A saving scheme" - the preview's `gold_savings_scheme`. A twelve-month
// timeline (customer pays months 1-11, the jeweller adds month 12) that
// fills as the months slider moves, with the totals underneath.
const SavingsScheme = ({
  ariaLabel,
  amountLabel,
  initialAmount,
  amountStep,
  monthsLabel,
  monthsAriaLabel,
  initialMonths,
  timelineLabel,
  customerLabel,
  jewellerLabel,
  pays,
  gets,
  adds,
  states,
}: SavingsSchemeData) => {
  const scheme = useSavingsScheme(initialAmount, initialMonths);
  const amountId = useId();
  const amount = formatInr(scheme.amount);
  const pct = `${(100 / MONTHS).toFixed(1)}%`;
  const [addsBefore, addsAfter] = adds.note.split("{pct}");
  const stateText =
    scheme.months === 0
      ? states.notStarted
      : scheme.months < 11
        ? states.paying
            .replace("{paid}", formatInr(scheme.paidSoFar))
            .replace("{total}", formatInr(scheme.customerPays))
        : scheme.months === 11
          ? states.allPaid
          : states.matured.replace("{total}", formatInr(scheme.customerGets));

  return (
    <figure role="group" aria-label={ariaLabel} className="m-0 grid min-w-0 gap-5">
      <div className="grid gap-[18px] rounded-[22px] bg-white px-[22px] py-[18px] md:grid-cols-2">
        <div className="grid gap-2 text-[15px] font-semibold">
          <label htmlFor={amountId}>{amountLabel}</label>
          <Input
            id={amountId}
            type="number"
            inputMode="numeric"
            min={500}
            max={1000000}
            step={amountStep}
            defaultValue={initialAmount}
            onChange={(event) => scheme.setAmount(event.target.value)}
            className="h-auto rounded-[14px] border-[1.5px] border-line px-3.5 py-3 text-[22px] font-bold text-ink focus-visible:border-primary md:text-[22px]"
          />
        </div>
        <div className="grid content-start gap-2 text-[15px] font-semibold">
          <span>
            {monthsLabel}
            <output className="ml-1.5 font-extrabold text-primary">{scheme.months}</output>
          </span>
          <Slider
            aria-label={monthsAriaLabel}
            min={0}
            max={MONTHS}
            step={1}
            value={[scheme.months]}
            onValueChange={([value]) => scheme.setMonths(value)}
          />
        </div>
      </div>

      <div
        role="img"
        aria-label={timelineLabel}
        className="relative grid grid-cols-4 gap-x-1.5 gap-y-3 py-1 sm:grid-cols-6 xl:grid-cols-12"
      >
        <span
          aria-hidden="true"
          className="absolute top-6 right-[4%] left-[4%] hidden h-1 rounded bg-line xl:block"
        />
        {Array.from({ length: MONTHS }, (_, index) => {
          const isJeweller = index === MONTHS - 1;
          const isOn = index < scheme.months;
          return (
            <span
              key={index}
              className="relative z-[1] grid justify-items-center gap-1 text-[11px] sm:text-[12px]"
            >
              <i
                className={cn(
                  "grid size-10 place-items-center rounded-full border-4 font-extrabold not-italic transition-colors duration-300 motion-reduce:transition-none",
                  isOn
                    ? isJeweller
                      ? "border-tone-sun-ink bg-tone-sun-ink text-white"
                      : "border-primary bg-primary text-white"
                    : "border-line bg-white text-ink-muted",
                )}
              >
                {index + 1}
              </i>
              <em
                className={cn(
                  "not-italic",
                  isJeweller ? "font-bold text-tone-sun-ink" : "text-ink-muted",
                )}
              >
                {isJeweller ? jewellerLabel : customerLabel}
              </em>
              <b className="text-[11.5px] font-bold sm:text-[12.5px]">{amount}</b>
            </span>
          );
        })}
      </div>

      <div className="grid gap-3.5 md:grid-cols-[1fr_1fr_1.4fr]">
        <div className={cn(cardClass, "bg-tone-blush")}>
          <small className={cardKeyClass}>{pays.label}</small>
          <b className={cardValueClass}>{formatInr(scheme.customerPays)}</b>
          <span className="text-[15px]">{pays.note}</span>
        </div>
        <div className={cn(cardClass, "bg-tone-mint")}>
          <small className={cardKeyClass}>{gets.label}</small>
          <b className={cardValueClass}>{formatInr(scheme.customerGets)}</b>
          <span className="text-[15px]">{gets.note}</span>
        </div>
        <div className={cn(cardClass, "bg-tone-lilac")}>
          <small className={cardKeyClass}>{adds.label}</small>
          <b className={cardValueClass}>{amount}</b>
          <span className="text-[15px]">
            {addsBefore}
            {pct}
            {addsAfter}
          </span>
        </div>
      </div>

      <p aria-live="polite" className="text-center text-base font-bold text-primary">
        {stateText}
      </p>
    </figure>
  );
};

export default SavingsScheme;
